import { router } from "@inertiajs/react"
import { ArchiveRestore } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import TiktokLinkStateCue from "@/components/tiktok-link-state-cue"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { TableCell, TableRow } from "@/components/ui/table"
import { useInitials } from "@/hooks/use-initials"
import { useOnline } from "@/hooks/use-online"
import { countryLabel } from "@/lib/countries"
import { cn } from "@/lib/utils"
import { candidatePath } from "@/routes"
import type { CandidateRow as CandidateRowType } from "@/types/candidates"

import { CandidateStatusBadge } from "./candidate-status-badge"

/**
 * OKS-127 — Recency-highlight class for the freshly-added row.
 *
 * Shared by both `<CandidateRow>` (desktop ≥md table) and
 * `<CandidateRowCard>` (mobile <md card) so the visual is identical
 * across viewports.
 *
 * Implementation notes:
 *   - `bg-accent/30` token (NOT `bg-success/10`): Crew theme has no
 *     `--success` token; UX Spec §9.2 pre-authorised the swap to a
 *     neutral accent.
 *   - `duration-[1500ms]` arbitrary value: Tailwind v4 default scale
 *     stops at `duration-1000`; arbitrary keeps the change out of
 *     `tailwind.config`.
 *   - `motion-safe:` prefix on every animation utility: under
 *     `prefers-reduced-motion: reduce`, the tint never paints at all
 *     (per UX Spec §6 — implementer picks the cleanest path; "skip
 *     entirely" matches the `motion-safe:` semantic). The URL marker
 *     is still scrubbed on mount in `Index.tsx`.
 *   - One-shot lifecycle: each row component holds `useState(true)`
 *     for `tinted`, then flips to `false` via a one-shot `useEffect`
 *     on mount → `transition-colors` carries the row from
 *     `bg-accent/30` back to transparent over 1500ms. No re-trigger
 *     on re-render because the state is local and gated by the
 *     initial `isJustAdded` value.
 */
const RECENCY_HIGHLIGHT_TINT_CLASSES =
  "motion-safe:bg-accent/30 motion-safe:transition-colors motion-safe:duration-[1500ms] motion-safe:ease-out"
const RECENCY_HIGHLIGHT_BASE_CLASSES =
  "motion-safe:transition-colors motion-safe:duration-[1500ms] motion-safe:ease-out"

/**
 * OKS-127 — Hook factoring the one-shot fade lifecycle for the
 * recency highlight. Returns the className fragment to append to the
 * row container. Both `<CandidateRow>` and `<CandidateRowCard>`
 * consume the same hook so the desktop + mobile experiences stay in
 * lockstep.
 *
 * Lifecycle:
 *   1. Mount with `isJustAdded={true}` → `tinted=true` → row renders
 *      with `bg-accent/30`.
 *   2. `useEffect` flips `tinted=false` on next paint → the
 *      transition-colors utility carries the background back to
 *      transparent over 1500ms.
 *   3. `isJustAdded={false}` (or absent) → no tint class is ever
 *      applied; transition utility is also omitted (no-op CSS).
 */
function useRecencyHighlightClasses(isJustAdded: boolean): string {
  const [tinted, setTinted] = useState(isJustAdded)

  useEffect(() => {
    if (!isJustAdded) return
    // Flip on the next microtask so the initial paint shows the tint;
    // the subsequent re-render triggers the transition-colors fade
    // back to transparent. Using a 0-delay `setTimeout` (rather than
    // calling `setTinted(false)` synchronously) guarantees React
    // commits the tinted frame before the transparent frame.
    const id = window.setTimeout(() => setTinted(false), 0)
    return () => window.clearTimeout(id)
  }, [isJustAdded])

  if (!isJustAdded) return ""
  return tinted
    ? RECENCY_HIGHLIGHT_TINT_CLASSES
    : RECENCY_HIGHLIGHT_BASE_CLASSES
}

/**
 * OKS-17 — Single row in the candidate list (desktop ≥640px). Mobile
 * branch is the `CandidateRowCard` below.
 *
 * **Whole-row click target.** Pressing Enter/Space when the row is
 * focused, or clicking anywhere on the row body, navigates to the
 * candidate's detail page via Inertia. UX Spec §Table — `aria-label`
 * announces "Open candidate {handle}".
 *
 * Locked testids:
 *   - `candidate-list-row` — every row (querySelectorAll target)
 *   - `candidate-list-row-{id}` — id-scoped row (Playwright targeting)
 *   - `candidate-list-row-status-{id}` — status badge inside row
 *
 * **OKS-19 additive — bulk selection.** When the parent passes a
 * `selection` prop the row renders a leading checkbox column
 * (`candidate-list-row-checkbox-{id}`). The checkbox cell stops
 * click/keydown propagation so the whole-row navigation isn't
 * triggered when the user actually intends to toggle selection.
 * When `selection` is undefined the row renders identically to its
 * OKS-17 shape (back-compat — Vitest specs that don't seed
 * selection still pass).
 *
 * Visual selection accent (UX Spec §Micro-Interactions): when the
 * row is selected, a 4px primary-coloured left border replaces the
 * default border (no layout shift — drawn inset).
 */
export interface CandidateSelectionApi {
  isSelected: (id: number) => boolean
  toggle: (id: number) => void
}

interface CandidateRowProps {
  candidate: CandidateRowType
  /**
   * OKS-19 — bulk-selection plumbing. When present, the row renders a
   * leading checkbox column. When absent, the row matches OKS-17 shape
   * verbatim. Index.tsx (F6) wires this in via `useBulkSelection`;
   * standalone tests (status-history, etc.) leave it unset.
   */
  selection?: CandidateSelectionApi
  /**
   * OKS-127 — recency-highlight flag. `true` on the row whose
   * `candidate.id` matches the `just_added_id` Inertia prop fed from
   * the post-create redirect. The row paints with `bg-accent/30` on
   * first render then fades to transparent over 1500ms ease-out
   * (skipped entirely under `prefers-reduced-motion: reduce`). When
   * `false` / `undefined` the row renders identically to its OKS-17
   * shape — no tint, no transition utility, no class drift.
   */
  isJustAdded?: boolean
  /**
   * OKS-465 — Removed-view mode. When true, the row renders a trailing
   * Restore action cell (driven by `candidate.restore_url`) instead of
   * any active-list affordances. The whole-row navigation to the
   * candidate detail is preserved (the removed detail page shows its own
   * Restore banner).
   */
  removedView?: boolean
}

export function CandidateRow({
  candidate,
  selection,
  isJustAdded = false,
  removedView = false,
}: CandidateRowProps) {
  const { t } = useTranslation()
  const initials = useInitials()
  const handleOpen = () => router.visit(candidatePath(candidate.id))
  const ariaLabel = t("candidates.list.row.open_candidate_aria", {
    handle: candidate.tiktok_handle,
  })
  const isSelected = selection?.isSelected(candidate.id) ?? false
  const recencyClasses = useRecencyHighlightClasses(isJustAdded)

  return (
    <TableRow
      data-testid={`candidate-list-row-${candidate.id}`}
      data-candidate-id={candidate.id}
      data-row="candidate-list-row"
      data-selected={isSelected ? "true" : undefined}
      // OKS-127 — stable hook for Playwright / Vitest to assert
      // recency-highlight state without relying on the volatile
      // `bg-accent/30` class string. `"true"` while the tint paints;
      // attribute absent otherwise.
      data-just-added={isJustAdded ? "true" : undefined}
      tabIndex={0}
      // Phase 4 F4 — `role="link"` is invalid on `<tr>` (no role
      // overlay for table rows; ARIA spec rejects it). Both desktop
      // <tr> and mobile <div> drop the role; tabIndex + aria-label +
      // key handler still provide keyboard + SR semantics.
      aria-label={ariaLabel}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleOpen()
        }
      }}
      className={cn(
        "hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
        // OKS-19 — selection accent. 4px inset primary border on the
        // left edge when selected (UX Spec §Micro-Interactions).
        // `border-l-0` on the unselected default keeps the layout
        // identical so the accent doesn't shift sibling cells.
        isSelected && "border-primary border-l-4",
        // OKS-127 — one-shot 1500ms fade from `bg-accent/30` →
        // transparent on the freshly-added row. No-op when
        // `isJustAdded={false}` or when `prefers-reduced-motion: reduce`
        // is set (the `motion-safe:` prefix gates the entire effect).
        recencyClasses,
      )}
    >
      {selection && (
        <SelectionCell
          variant="row"
          candidate={candidate}
          isSelected={isSelected}
          onToggle={() => selection.toggle(candidate.id)}
        />
      )}
      <TableCell className="font-medium">
        <div className="flex items-center gap-3">
          <Avatar className="size-8 shrink-0">
            <AvatarFallback>{initials(candidate.display_name)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-mono text-sm">
                @{candidate.tiktok_handle}
              </span>
              {/* OKS-23 — link-state cue for non-active states. Renders
                  nothing when `tiktok_link_state` is null (active links
                  keep the roster quiet per UX Spec §1.E). */}
              <TiktokLinkStateCue state={candidate.tiktok_link_state ?? null} />
            </div>
            <span className="text-muted-foreground truncate text-xs">
              {candidate.display_name}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground text-right tabular-nums">
        {candidate.approx_followers === null
          ? "—"
          : new Intl.NumberFormat().format(candidate.approx_followers)}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <CandidateStatusBadge
            status={candidate.status}
            testId={`candidate-list-row-status-${candidate.id}`}
          />
          <RejectionReasonBadge candidate={candidate} />
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground hidden md:table-cell">
        <LocationCell location={candidate.location} />
      </TableCell>
      {/*
        OKS-946 — the other half of the coupled fix (the first is removing
        `whitespace-nowrap` from `ui/badge.tsx`). BOTH classes are required
        and neither works alone:

          `max-w-[220px]`      gives the badges something to wrap WITHIN.
                               `candidate-table.tsx` is `table-layout: auto`
                               with no column caps, so without a bound the
                               column sizes itself to the badge's max-content
                               and the badge escapes the scroll container —
                               measured at 85px past its right edge.
          `whitespace-normal`  is inherited BY the badges. `ui/table.tsx`'s
                               `TableCell` declares `whitespace-nowrap`, so a
                               badge that declares none would otherwise inherit
                               NOWRAP here and still refuse to wrap.

        ⚠️ `whitespace-normal` belongs on the CELL, not on the `<Badge>`.
        A badge-level override would be the FOURTH call-site override of this
        primitive, which is the practice this ticket exists to end.

          `wrap-anywhere`      (`overflow-wrap: anywhere`) closes AC1 for a tag
                               with NO space or hyphen. Tags have no
                               server-side length cap, so that input is
                               reachable, and `whitespace-normal` alone cannot
                               wrap it — measured 404px, one line, overflowing.

        ⚠️ `break-words` does NOT work here and was measured, not assumed:
        `overflow-wrap: break-word` permits breaking but does not reduce the
        element's `min-content`, so `w-fit` still resolves to the full token
        width. `anywhere` reduces `min-content`; `break-word` does not. Both
        are inert for ordinary spaced text (control: identical 220px / 2 lines
        with and without).

        Guarded, not hoped: AC4's escape arm
        (`tests/playwright/oks946-badge-geometry.spec.ts`) reds if either of
        the first two classes is removed. ⚠️ `wrap-anywhere` is NOT covered by
        that arm — its fixture uses spaced text. Measured in isolation with a
        control; no in-app arm exercises a single-token tag.
      */}
      <TableCell className="hidden max-w-[220px] wrap-anywhere whitespace-normal lg:table-cell">
        <TagsCell tags={candidate.tags} />
      </TableCell>
      <TableCell className="text-muted-foreground tabular-nums">
        <RelativeDays at={candidate.created_at} />
      </TableCell>
      {removedView && (
        <TableCell
          className="text-right"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <RowRestoreAction candidate={candidate} />
        </TableCell>
      )}
    </TableRow>
  )
}

/**
 * Mobile (<640px) — card layout per UX Spec §Table mobile column.
 * Same data, stacked. Same testids as the desktop row so QA Playwright
 * can target a single selector across viewports.
 */
export function CandidateRowCard({
  candidate,
  selection,
  isJustAdded = false,
  removedView = false,
}: CandidateRowProps) {
  const { t } = useTranslation()
  const initials = useInitials()
  const handleOpen = () => router.visit(candidatePath(candidate.id))
  const ariaLabel = t("candidates.list.row.open_candidate_aria", {
    handle: candidate.tiktok_handle,
  })
  const isSelected = selection?.isSelected(candidate.id) ?? false
  const recencyClasses = useRecencyHighlightClasses(isJustAdded)

  return (
    <div
      data-testid={`candidate-list-row-${candidate.id}`}
      data-candidate-id={candidate.id}
      data-row="candidate-list-row"
      data-selected={isSelected ? "true" : undefined}
      // OKS-127 — mirror the desktop row's stable assertion hook on
      // the mobile card so Playwright / Vitest can target a single
      // selector across viewports.
      data-just-added={isJustAdded ? "true" : undefined}
      tabIndex={0}
      // Phase 4 F4 — `role="link"` is invalid on `<tr>` (no role
      // overlay for table rows; ARIA spec rejects it). Both desktop
      // <tr> and mobile <div> drop the role; tabIndex + aria-label +
      // key handler still provide keyboard + SR semantics.
      aria-label={ariaLabel}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleOpen()
        }
      }}
      className={cn(
        "hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-ring flex min-h-[72px] cursor-pointer items-start gap-3 border-b p-4 focus-visible:ring-2 focus-visible:outline-none",
        // OKS-19 — selection accent on the mobile card matches the
        // desktop row: 4px primary left border when selected.
        isSelected && "border-l-primary border-l-4",
        // OKS-127 — identical recency-highlight fade applied to the
        // mobile card. `useRecencyHighlightClasses` returns the empty
        // string when `isJustAdded={false}` so the OKS-17 mobile
        // baseline is preserved verbatim.
        recencyClasses,
      )}
    >
      {selection && (
        <SelectionCell
          variant="card"
          candidate={candidate}
          isSelected={isSelected}
          onToggle={() => selection.toggle(candidate.id)}
        />
      )}
      <Avatar className="size-10 shrink-0">
        <AvatarFallback>{initials(candidate.display_name)}</AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-mono text-sm">
              @{candidate.tiktok_handle}
            </span>
            <span className="text-muted-foreground truncate text-xs">
              {candidate.display_name}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <CandidateStatusBadge
              status={candidate.status}
              testId={`candidate-list-row-status-${candidate.id}`}
            />
            <RejectionReasonBadge candidate={candidate} />
          </div>
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 text-xs">
          <span className="tabular-nums">
            {candidate.approx_followers === null
              ? "—"
              : new Intl.NumberFormat().format(candidate.approx_followers)}
          </span>
          <span>•</span>
          <RelativeDays at={candidate.created_at} />
        </div>
        {removedView && (
          <div
            className="mt-2"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <RowRestoreAction candidate={candidate} />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── shared cells ──────────────────────────────────────────────────

/**
 * OKS-465 — Per-row Restore action for the Removed view (shared by the
 * desktop row + mobile card). Renders nothing when `candidate.restore_url`
 * is null/absent (URL-nullability rule — non-permitted viewers). Restore
 * is confirmed via a shadcn AlertDialog (no reason field) and fires a
 * programmatic POST; the ≥44px button meets the touch-target floor.
 */
function RowRestoreAction({ candidate }: { candidate: CandidateRowType }) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const url = candidate.restore_url

  if (!url) return null

  function submitRestore() {
    if (!url || submitting || !online) return
    setSubmitting(true)
    router.post(
      url,
      {},
      {
        preserveScroll: true,
        onSuccess: () => setOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid={`candidate-list-row-restore-${candidate.id}`}
        aria-label={t("candidates.list.removed_view.restore_aria", {
          handle: candidate.tiktok_handle,
        })}
        disabled={submitting || !online}
        className="min-h-11 sm:min-h-9"
        onClick={() => setOpen(true)}
      >
        <ArchiveRestore className="size-4" aria-hidden="true" />
        {t("candidates.list.removed_view.restore")}
      </Button>
      <AlertDialogContent
        data-testid={`candidate-list-row-restore-dialog-${candidate.id}`}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("candidates.list.removed_view.restore_dialog_title", {
              handle: candidate.tiktok_handle,
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("candidates.list.removed_view.restore_dialog_description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>
            {t("candidates.list.removed_view.restore_dialog_cancel")}
          </AlertDialogCancel>
          {/* OKS-1023 — plain `Button`, not the closing `AlertDialogAction`:
              the dialog holds while the POST is in flight and closes in
              `submitRestore`'s `onSuccess`. */}
          <Button
            type="button"
            data-testid={`candidate-list-row-restore-confirm-${candidate.id}`}
            disabled={submitting || !online}
            onClick={submitRestore}
          >
            {t("candidates.list.removed_view.restore_dialog_confirm")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * OKS-19 — leading checkbox cell shared by the desktop `<TableRow>`
 * and the mobile `<div>` card. The two variants differ only in the
 * outer wrapper element + padding so click-region semantics match the
 * surrounding row layout.
 *
 * **Click semantics (CRITICAL):** the cell stops propagation on
 * `onClick` and `onKeyDown` — without that the whole-row Inertia
 * visit would fire whenever the user clicks the checkbox. Radix
 * `<Checkbox>` itself uses `onCheckedChange` (separate from
 * `onClick`), so the wrapper's `stopPropagation` only blocks the
 * outer row's click handler from running on the same event. Mouse
 * pointer events that miss the checkbox itself but hit the cell
 * padding — those still navigate (intentional: large click target
 * for the row, focused click target for the checkbox).
 */
interface SelectionCellProps {
  variant: "row" | "card"
  candidate: CandidateRowType
  isSelected: boolean
  onToggle: () => void
}

function SelectionCell({
  variant,
  candidate,
  isSelected,
  onToggle,
}: SelectionCellProps) {
  const { t } = useTranslation()
  const ariaLabel = t("candidates.list.bulk.row_checkbox_aria", {
    handle: candidate.tiktok_handle,
  })
  const checkbox = (
    <Checkbox
      data-testid={`candidate-list-row-checkbox-${candidate.id}`}
      aria-label={ariaLabel}
      checked={isSelected}
      onCheckedChange={() => onToggle()}
      // Stop propagation on raw click so a stray pointer event on the
      // checkbox button doesn't bubble up and trigger the row's
      // navigation handler. Radix's onCheckedChange already handles
      // the canonical toggle.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    />
  )

  if (variant === "row") {
    return (
      <TableCell
        className="w-11 pr-1 pl-3"
        // Cell-level stopPropagation guards against pointer events
        // landing on the cell padding (not the checkbox itself) — those
        // would otherwise navigate. UX Spec §A11y: 44px touch target.
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {checkbox}
      </TableCell>
    )
  }

  return (
    <div
      className="-ml-1 flex size-11 shrink-0 items-center justify-center"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {checkbox}
    </div>
  )
}

/**
 * OKS-348 — Composite text rendering for a row's location.
 * Renders `<City>, <Localized Country Name>` (the country half is
 * resolved from the ISO alpha-2 code via `countryLabel(code, locale)`).
 * Falls back to bare city, bare localized country, or `—` when the
 * other half is missing.
 */
function formatLocation(
  location: CandidateRowType["location"],
  locale: "en" | "fr",
): string {
  const { city, country } = location
  const countryName = country ? countryLabel(country, locale) : null
  if (city && countryName) return `${city}, ${countryName}`
  if (countryName) return countryName
  if (city) return city
  return "—"
}

/**
 * OKS-348 — Cell wrapper that renders the localized location text.
 */
function LocationCell({
  location,
}: {
  location: CandidateRowType["location"]
}) {
  const { i18n } = useTranslation()
  const locale = i18n.language.startsWith("fr") ? "fr" : "en"
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{formatLocation(location, locale)}</span>
    </span>
  )
}

function TagsCell({ tags }: { tags: string[] }) {
  const { t } = useTranslation()
  if (tags.length === 0) {
    return (
      <span className="text-muted-foreground" aria-hidden="true">
        —
      </span>
    )
  }
  const visible = tags.slice(0, 2)
  const overflow = tags.length - visible.length
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((tag) => (
        <Badge key={tag} variant="secondary" className="font-normal">
          {tag}
        </Badge>
      ))}
      {overflow > 0 && (
        <Badge variant="outline" className="text-muted-foreground font-normal">
          {t("candidates.list.row.tags_overflow", { count: overflow })}
        </Badge>
      )}
    </div>
  )
}

function RelativeDays({ at }: { at: string }) {
  const { t } = useTranslation()
  const { text, title } = formatRelativeDays(at, t)
  return (
    <time dateTime={at} title={title}>
      {text}
    </time>
  )
}

/**
 * Pure-as-far-as-this-render-cares helper: takes the raw timestamp
 * and an `i18next` translator, returns formatted strings. `Date.now()`
 * lives here at module scope (outside the React component closure) so
 * the `react-hooks/purity` rule treats it as a non-component impurity
 * — which is the right model for "snapshot the wall clock at render".
 *
 * Pattern matches `relativeTime` in `pages/Agency/Team/Index.tsx`,
 * `pages/Brand/Team/Index.tsx` etc.
 */
function formatRelativeDays(
  at: string,
  t: ReturnType<typeof useTranslation>["t"],
): { text: string; title: string } {
  const date = new Date(at)
  const ms = Date.now() - date.getTime()
  const days = Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
  let text: string
  if (days < 7) {
    text = t("candidates.list.row.days_added_relative_d", { n: days })
  } else if (days < 60) {
    const weeks = Math.floor(days / 7)
    text = t("candidates.list.row.days_added_relative_w", { n: weeks })
  } else {
    const months = Math.floor(days / 30)
    text = t("candidates.list.row.days_added_relative_mo", { n: months })
  }
  return { text, title: date.toLocaleDateString() }
}

/**
 * OKS-952 — the coded reason behind a rejection, beside the status badge.
 *
 * Renders NOTHING when there is no code: non-rejected rows, pre-taxonomy
 * rejections, and rejections made without one (it is optional). Deliberately
 * no "unknown reason" placeholder — the reason was never asked for, so
 * asserting it is unknown would state a falsehood to fill a gap.
 *
 * The label is resolved from the raw enum through the shared
 * `candidates.reason_code.*` namespace, the same keys the picker and the
 * timeline use — one label per code across the whole product.
 */
function RejectionReasonBadge({ candidate }: { candidate: CandidateRowType }) {
  const { t } = useTranslation()
  const code = candidate.rejection_reason_code

  if (!code) return null

  return (
    <Badge
      variant="outline"
      className="font-normal"
      data-testid={`candidate-list-row-reason-${candidate.id}`}
      data-reason-code={code}
    >
      {t(`candidates.reason_code.${code}`)}
    </Badge>
  )
}
